import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { organizationApi } from '../services/api';
import type { Organization } from '@taskfly/shared/types';
import { websocketService } from '../services/websocket';
import { useAiChatStore } from './aiChatStore';
import i18n from '../i18n';

interface OrganizationState {
  activeOrganizationId: string | null;
  organizations: Organization[];
  isLoading: boolean;
  error: string | null;
  
  fetchOrganizations: () => Promise<void>;
  setActiveOrganization: (orgId: string) => Promise<void>;
  createOrganization: (name: string) => Promise<Organization | null>;
  switchOrganization: (orgId: string) => void;
  clearError: () => void;
}

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set) => ({
      activeOrganizationId: null,
      organizations: [],
      isLoading: false,
      error: null,

      fetchOrganizations: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await organizationApi.list();
          if (response.success && response.data) {
            const orgs = (response.data as Organization[]).map(org => ({
              ...org,
              memberCount: (org as any)._count?.members ?? org.memberCount,
            }));
            set({ organizations: orgs, isLoading: false });
          } else {
            set({ error: i18n.t('organizations.error.fetch'), isLoading: false });
          }
        } catch {
          set({ error: i18n.t('organizations.error.fetch'), isLoading: false });
        }
      },

      setActiveOrganization: async (orgId: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await organizationApi.setActive(orgId);
          if (response.success) {
            set({ activeOrganizationId: orgId, isLoading: false });
            // REV-02 fix: Switch WebSocket room when active organization changes
            websocketService.switchOrg(orgId);

            // Close and reset AI assistant chat context on switching organization
            const chatStore = useAiChatStore.getState();
            chatStore.closeChat();
            chatStore.clearCurrentConversation();
            useAiChatStore.setState({ conversations: [] });
          } else {
            set({ error: i18n.t('organizations.error.setActive'), isLoading: false });
          }
        } catch {
          set({ error: i18n.t('organizations.error.setActive'), isLoading: false });
        }
      },

      createOrganization: async (name: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await organizationApi.create(name);
          if (response.success && response.data) {
            const org = response.data as Organization;
            set(state => ({
              organizations: [...state.organizations, org],
              activeOrganizationId: org.id,
              isLoading: false,
            }));
            // REV-2.1: Join WebSocket room for newly created org (same bug class as REV-02)
            websocketService.switchOrg(org.id);

            // Close and reset AI assistant chat context on switching organization
            const chatStore = useAiChatStore.getState();
            chatStore.closeChat();
            chatStore.clearCurrentConversation();
            useAiChatStore.setState({ conversations: [] });
            return org;
          } else {
            set({ error: i18n.t('organizations.error.create'), isLoading: false });
            return null;
          }
        } catch {
          set({ error: i18n.t('organizations.error.create'), isLoading: false });
          return null;
        }
      },

      switchOrganization: (orgId: string) => {
        set({ activeOrganizationId: orgId });
        // REV-02 fix: Switch WebSocket room when active organization changes
        websocketService.switchOrg(orgId);

        // Close and reset AI assistant chat context on switching organization
        const chatStore = useAiChatStore.getState();
        chatStore.closeChat();
        chatStore.clearCurrentConversation();
        useAiChatStore.setState({ conversations: [] });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'organization-storage',
      partialize: (state) => ({ 
        activeOrganizationId: state.activeOrganizationId,
        organizations: state.organizations,
      }),
    }
  )
);